import { useEffect, useRef } from 'react';
import chrome from '../generated/marketingChrome.json';

/**
 * Renders a SNAPSHOT of the WordPress marketing header/footer inside a
 * Shadow DOM, so the WP markup + CSS are fully isolated from the React app.
 *
 * Why this is safe (unlike the old runtime WPFragment):
 *   - The HTML is a static snapshot baked at build time by updateheader.sh —
 *     no runtime fetch, no dependency on WordPress being up.
 *   - It renders in a Shadow DOM: WP stylesheets apply ONLY inside the shadow
 *     tree and can never bleed into the page (which previously broke the
 *     login/register forms).
 *   - Setting shadowRoot.innerHTML does NOT execute <script> tags (HTML spec),
 *     so WP's jQuery/Elementor/gtranslate scripts stay inert — no crashes.
 *
 * To refresh the snapshot: run ./updateheader.sh then rebuild/deploy.
 *
 * Props:
 *   - type: 'header' | 'footer'
 *   - fallback: React node rendered if the snapshot is missing / Shadow DOM
 *     is unsupported (keeps the page functional no matter what).
 */
export default function WPChrome({ type, fallback = null }) {
  const hostRef = useRef(null);
  const html = (chrome && chrome[type]) || '';

  // Register WP fonts at DOCUMENT level (once). @font-face does not work inside
  // a Shadow DOM, so the bold/heading weights would never load otherwise.
  // Injecting font-face/links globally only defines font families — it does not
  // restyle the React app (nothing references these family names).
  useEffect(() => {
    const fonts = chrome && chrome.fonts;
    if (!fonts || typeof document === 'undefined') return;
    if (document.getElementById('wpchrome-fonts')) return;
    const frag = document.createDocumentFragment();
    (fonts.links || []).forEach((href) => {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      l.className = 'wpchrome-font-link';
      frag.appendChild(l);
    });
    if (fonts.faces) {
      const s = document.createElement('style');
      s.id = 'wpchrome-fonts';
      s.textContent = fonts.faces;
      frag.appendChild(s);
    }
    document.head.appendChild(frag);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || !html || !host.attachShadow) return;
    let root = host.shadowRoot;
    if (!root) {
      root = host.attachShadow({ mode: 'open' });
    }
    // innerHTML loads <link>/<style> scoped to the shadow tree; <script> tags
    // are parsed but NOT executed — exactly the isolation we want.
    root.innerHTML = html;

    // Base responsive guard: WP header/footer snapshots contain fixed-size
    // images (e.g. a 768px footer graphic) that overflow the viewport on mobile,
    // creating horizontal scroll. The page's WP custom CSS can't reach into the
    // shadow tree, so constrain images + enforce box-sizing here.
    if (!root.querySelector('style[data-wpchrome-base]')) {
      const base = document.createElement('style');
      base.setAttribute('data-wpchrome-base', '');
      base.textContent = `:host,*{box-sizing:border-box} img{max-width:100%;height:auto}`;
      root.appendChild(base);
    }

    // Brand logo sizing. The snapshot renders it at ~40px tall, which reads as
    // tiny next to the nav. Double it. The source file is 984x546, so this is
    // still downscaling — no blur. Slightly smaller on phones so it does not
    // crowd the burger menu. `!important` + max-width:none are needed to beat
    // the theme's own width rule and the base `max-width:100%` above.
    if (type === 'header' && !root.querySelector('style[data-wpchrome-logo]')) {
      const logo = document.createElement('style');
      logo.setAttribute('data-wpchrome-logo', '');
      logo.textContent = `
        img[src*="Trados-Trade-Verified"] {
          width: 158px !important;
          height: auto !important;
          max-width: none !important;
        }
        @media (max-width: 640px) {
          img[src*="Trados-Trade-Verified"] { width: 118px !important; }
        }
      `;
      root.appendChild(logo);
    }

    // WP nav dropdowns are normally JS-driven (Elementor), and we don't run
    // scripts. Add a CSS-only hover fallback so submenus still reveal. Scoped
    // to the shadow tree, so it can't affect the rest of the app.
    if (type === 'header' && !root.querySelector('style[data-wpchrome-dropdown]')) {
      const s = document.createElement('style');
      s.setAttribute('data-wpchrome-dropdown', '');
      // The theme's combined CSS already styles the dropdown panel/items
      // (white bg, border, shadow, compact padding). We only need to REVEAL it
      // on hover, since Elementor normally toggles it via JS (which we don't
      // run). Do NOT restyle the panel — that fought the theme CSS and caused
      // the layout mess. Plus a symmetric header gutter.
      // Complete dropdown override. Prefixed with .elementor-location-header to
      // MATCH the theme's specificity (its rules also use that ancestor); since
      // this <style> is appended after the theme CSS, our !important wins ties.
      // Fixes: no panel bg, oversized item gaps, and anchors the panel under
      // its parent item. We must own this fully because Elementor's JS (which
      // normally positions/animates the dropdown) does not run here.
      s.textContent = `
        .elementor-location-header .elementor-nav-menu--main .menu-item-has-children { position: relative !important; }
        .elementor-location-header .elementor-nav-menu--dropdown {
          display: none !important;
          position: absolute !important; top: 100% !important; left: 0 !important; right: auto !important;
          width: max-content !important; min-width: 210px !important; max-width: 340px !important;
          background: #ffffff !important; border: 1px solid #e2e8f0 !important; border-radius: 12px !important;
          box-shadow: 0 8px 32px rgba(0,0,0,.12) !important; margin: 8px 0 0 0 !important; padding: 6px !important;
          z-index: 9999 !important; list-style: none !important; max-height: none !important; overflow: visible !important;
        }
        .elementor-location-header .menu-item-has-children:hover > .elementor-nav-menu--dropdown {
          display: block !important; opacity: 1 !important; visibility: visible !important; pointer-events: auto !important;
        }
        /* Transparent bridge over the 8px gap so moving the mouse from the menu
           item down to the panel doesn't drop :hover and close the dropdown. */
        .elementor-location-header .menu-item-has-children > .elementor-nav-menu--dropdown::before {
          content: "" !important; position: absolute !important; left: 0 !important; right: 0 !important;
          top: -12px !important; height: 12px !important; background: transparent !important; display: block !important;
        }
        .elementor-location-header .elementor-nav-menu--dropdown li { margin: 0 !important; padding: 0 !important; }
        .elementor-location-header .elementor-nav-menu--dropdown li a,
        .elementor-location-header .elementor-nav-menu--dropdown .elementor-sub-item {
          display: block !important; padding: 10px 16px !important; margin: 0 !important;
          min-height: 0 !important; height: auto !important; line-height: 1.3 !important;
          font-size: 15px !important; border-radius: 8px !important; white-space: nowrap !important;
        }
        .elementor-location-header .elementor-nav-menu--dropdown li a:hover,
        .elementor-location-header .elementor-nav-menu--dropdown .elementor-sub-item:hover { background: #f3f4f6 !important; }
        .elementor-location-header .elementor-element-c14b629 {
          padding-left: 48px !important; padding-right: 48px !important; box-sizing: border-box;
        }
        /* Guarantee the logo + its whole wrapper chain are visible and sized in
           the shadow tree (a collapsed/overflow-hidden wrapper was blanking it). */
        .elementor-location-header .elementor-widget-theme-site-logo,
        .elementor-location-header .elementor-widget-theme-site-logo .elementor-widget-container,
        .elementor-location-header .elementor-widget-theme-site-logo a,
        .elementor-location-header .elementor-widget-image,
        .elementor-location-header .elementor-widget-image .elementor-widget-container,
        .elementor-location-header .elementor-widget-image a {
          display: block !important; opacity: 1 !important; visibility: visible !important;
          height: auto !important; max-height: none !important; min-height: 0 !important;
          overflow: visible !important; width: auto !important;
        }
        .elementor-location-header .elementor-widget-theme-site-logo img,
        .elementor-location-header .elementor-widget-image img {
          display: block !important; opacity: 1 !important; visibility: visible !important;
          height: 40px !important; width: auto !important; max-width: none !important; max-height: none !important;
        }`;
      root.appendChild(s);
    }
  }, [html, type]);

  // No snapshot, or no Shadow DOM support -> render the React fallback.
  if (!html || (typeof Element !== 'undefined' && !Element.prototype.attachShadow)) {
    return fallback;
  }

  return <div ref={hostRef} className={`wp-chrome wp-chrome-${type}`} data-testid={`wp-chrome-${type}`} />;
}
